import { theme } from '@config/theme';
import { useAppbarTitle } from '@hooks/useAppbarTitle';
import { useWindowSize } from '@hooks/useWindowSize';
import { Box, useMediaQuery } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { chatService } from 'services/chat';
import { useUsersStore } from 'services/users';
import useAuthenticatedWebSocket from 'services/websocket/authWebSocket';
import { WebSocketEventType, WebSocketMessage } from 'services/websocket/events';
import ChatSidebar from './ChatSidebar/ChatSidebar';
import { ChatWindow } from './ChatWindow';
import { useUserChatQueryParams } from './hooks/useChatQueryParams';
import { useZustandWebSocket } from './hooks/useZustandChat';
import { useChatStore, useMessageStore } from './store';
import { MessageContent, MessageContentType } from './types/messageContent';
import { MessageStatus } from './types/types';

export const ChatPageWrapper = () => {
	useAppbarTitle('chat');

	const { selectedChannelId, setSelectedChannelId, setLastMessageInfo } = useChatStore();
	const { user } = useUsersStore();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { channelId: urlChannelId } = useUserChatQueryParams();
	const [searchParams, setSearchParams] = useSearchParams();

	// Use new Zustand WebSocket handlers
	const { handleNewMessage, handleEditedMessage, handleDeletedMessage, handleMessageConfirmation } =
		useZustandWebSocket();

	// Track processed message IDs to prevent duplicate processing
	const processedMessageIds = useRef<Record<string, boolean>>({});
	
	// Track processed edit/delete events to prevent infinite loops
	const processedEditEvents = useRef<Record<string, number>>({});
	const processedDeleteEvents = useRef<Record<string, number>>({});

	// Track server responses to compare with WebSocket messages
	const serverResponses = useRef<Map<string, any>>(new Map());

	// Function to store server response for duplicate detection
	const storeServerResponse = (messageData: any) => {
		if (messageData && messageData.id) {
			console.log('📦 Storing server response for duplicate detection:', messageData.id);
			serverResponses.current.set(messageData.id, messageData);
			// Clean up after 10 seconds to prevent memory leaks
			setTimeout(() => {
				if (serverResponses.current.has(messageData.id)) {
					console.log('🧹 Cleaning up stored server response:', messageData.id);
					serverResponses.current.delete(messageData.id);
				}
			}, 10000);
		}
	};

	// Make it globally accessible for MessageInput
	useEffect(() => {
		(window as any).storeServerResponse = storeServerResponse;
		return () => {
			delete (window as any).storeServerResponse;
		};
	}, []);

	// Cleanup processed events periodically to prevent memory leaks
	useEffect(() => {
		const cleanupInterval = setInterval(() => {
			const now = Date.now();
			const cutoff = now - 60000; // Remove events older than 1 minute

			// Clean up processed edit events
			Object.keys(processedEditEvents.current).forEach(key => {
				if (processedEditEvents.current[key] < cutoff) {
					delete processedEditEvents.current[key];
				}
			});

			// Clean up processed delete events
			Object.keys(processedDeleteEvents.current).forEach(key => {
				if (processedDeleteEvents.current[key] < cutoff) {
					delete processedDeleteEvents.current[key];
				}
			});
		}, 30000); // Run cleanup every 30 seconds

		return () => clearInterval(cleanupInterval);
	}, []);

	// Handle URL channelId param on initial load
	useEffect(() => {
		const handleUrlChannelId = async () => {
			if (urlChannelId && urlChannelId !== selectedChannelId) {
				try {
					// Fetch the channel to make sure it exists
					const channel = await chatService.getChannel(urlChannelId);
					if (channel) {
						// Valid channel found, select it
						setSelectedChannelId(urlChannelId);
					}
				} catch (error) {
					console.error('Error fetching channel from URL param:', error);
					// Remove invalid channelId from URL
					searchParams.delete('channelId');
					searchParams.delete('channel');
					setSearchParams(searchParams);
				}
			}
		};

		handleUrlChannelId();
	}, [urlChannelId, selectedChannelId, setSelectedChannelId, searchParams, setSearchParams]);

	// WebSocket message handler using new Zustand integration
	useAuthenticatedWebSocket({
		onMessage: (message: WebSocketMessage) => {
			console.log('🔵 WebSocket message received:', message.messageType, message.payload);
			
			// Extract the ID from the payload - handle different field names for different message types
			const payload = message.payload || {};
			let id = '';
			
			if (message.messageType === WebSocketEventType.DeleteMessage) {
				// Delete messages use 'message_id' field
				id = typeof payload.message_id === 'string' ? payload.message_id : '';
			} else {
				// New messages and edit messages use 'id' field
				id = typeof payload.id === 'string' ? payload.id : '';
			}

			// For edit and delete messages, we should allow reprocessing
			// Only skip duplicates for new messages to avoid double-adding
			const shouldSkipDuplicate = message.messageType === WebSocketEventType.NewMessage && 
				id && processedMessageIds.current[id];
			
			if (shouldSkipDuplicate) {
				console.log('🟡 Skipping duplicate new message:', id);
				return;
			}

			// Mark as processed for new messages only
			if (id && message.messageType === WebSocketEventType.NewMessage) {
				processedMessageIds.current[id] = true;
			}

			// Handle new message
			if (message.messageType === WebSocketEventType.NewMessage) {
				if (id && payload.channel_id) {
					// Check if this message came from a server response we already handled
					if (serverResponses.current.has(id)) {
						console.log('🟡 Skipping WebSocket message - already processed via API response:', id);
						serverResponses.current.delete(id); // Clean up
						return;
					}

					// Check if this message already exists in the store
					const channelMessages = useMessageStore.getState().getChannelMessages(payload.channel_id);
					const existingMessage = channelMessages.find((msg) => msg.id === id);

					if (existingMessage) {
						console.log('🟡 Skipping WebSocket message - already exists in store:', id);
						return;
					}

					// Create message contents from payload
					let messageContents: MessageContent[] = [];
					try {
						if (payload.content) {
							// First try to decode as base64
							let decodedContent;
							try {
								decodedContent = atob(payload.content);
							} catch (base64Error) {
								decodedContent = payload.content;
							}

							// Then try to parse as JSON
							try {
								const contentArray = JSON.parse(decodedContent);
								if (Array.isArray(contentArray)) {
									messageContents = contentArray;
								}
							} catch (jsonError) {
								// If it's not JSON, create a simple text message content
								messageContents = [
									new MessageContent(
										MessageContentType.Text,
										decodedContent,
										[], // mentions
										{} // formatting
									),
								];
							}
						}
					} catch (error) {
						console.error('Error parsing message content:', error);
						// Fallback to empty content
						messageContents = [];
					}

					// Build author object - DO NOT use current user as fallback for missing author_id
					// This prevents messages from other users appearing as if sent by current user
					const authorId = payload.author.id;
					if (!authorId) {
						console.error('Message received without author_id, skipping:', payload);
						return; // Skip messages without proper author information
					}

					const authorDto = {
						id: authorId,
						name:
							payload.author_name ||
							(authorId === user?.user_id ? user?.first_name : 'Unknown') ||
							'Unknown',
						surname:
							payload.author_surname || (authorId === user?.user_id ? user?.last_name : '') || '',
						avatar:
							payload.author_avatar ||
							(authorId === user?.user_id ? user?.profile_image : '') ||
							'',
						onlineStatus: payload.author_online_status || 0,
					};

					// Create the message object in API format
					// Keep timestamps in seconds (Unix timestamp format) as per API standard
					const createdAt = payload.timestamp || Math.floor(Date.now() / 1000);
					
					const apiMessage = {
						id,
						channelId: payload.channel_id,
						content: payload.content,
						repliedMessageId: payload.replied_message_id,
						author: authorDto,
						mentions: payload.mentions || [],
						attachments: payload.attachments || [],
						createdAt: createdAt,
						status: MessageStatus.Active,
					};

					// Check if this message was sent by the current user and remove from pending
					const isOwnMessage = user && authorId === user.user_id;
					if (isOwnMessage) {
						console.log('🔵 Removing own message from pending via WebSocket:', id);
						// Remove from pending messages since it's now confirmed
						const { removePendingMessage } = useMessageStore.getState();
						removePendingMessage(payload.channel_id, id);
					}

					// Handle with Zustand
					console.log('🔵 Adding message via WebSocket:', id);
					handleNewMessage(apiMessage);

					// Update lastMessageInfo to trigger channel list update
					setLastMessageInfo(payload.channel_id);
				}
			}

			// Handle delete message
			else if (message.messageType === WebSocketEventType.DeleteMessage) {
				console.log('🔴 Processing delete message:', { id, channel_id: payload.channel_id });
				
				// Prevent duplicate processing of delete events
				const deleteEventKey = `${id}_${payload.channel_id}`;
				const now = Date.now();
				const lastProcessed = processedDeleteEvents.current[deleteEventKey];
				
				// Skip if processed within the last 1 second to prevent infinite loops
				if (lastProcessed && (now - lastProcessed) < 1000) {
					console.log('🟡 Skipping duplicate delete message:', deleteEventKey);
					return;
				}
				
				if (id && payload.channel_id) {
					// Mark as processed
					processedDeleteEvents.current[deleteEventKey] = now;
					// Handle with Zustand
					handleDeletedMessage(id, payload.channel_id);

					// Update lastMessageInfo to trigger UI updates for channel list	
					setLastMessageInfo(payload.channel_id);
					
					console.log('✅ Delete message processed successfully');
				} else {
					console.error('❌ Delete message missing required data:', { id, channel_id: payload.channel_id, payload });
				}
			}

			// Handle edit message
			else if (
				message.messageType === WebSocketEventType.EditMessage ||
				message.messageType === 'editMessage'
			) {
				console.log('🟠 Processing edit message:', { 
					id, 
					channel_id: payload.channel_id, 
					hasAuthor: !!payload.author?.id,
					payload 
				});
				
				// Prevent duplicate processing of edit events
				const editEventKey = `${id}_${payload.channel_id}`;
				const now = Date.now();
				const lastProcessed = processedEditEvents.current[editEventKey];
				
				// Skip if processed within the last 1 second to prevent infinite loops
				if (lastProcessed && (now - lastProcessed) < 1000) {
					console.log('🟡 Skipping duplicate edit message:', editEventKey);
					return;
				}
				
				// Make author validation more flexible - edit messages might not always have full author data
				if (id && payload.channel_id) {
					// Mark as processed
					processedEditEvents.current[editEventKey] = now;
					// Parse message contents
					let messageContents: MessageContent[] = [];
					try {
						if (payload.content) {
							// First try to decode as base64
							let decodedContent;
							try {
								decodedContent = atob(payload.content);
							} catch (base64Error) {
								decodedContent = payload.content;
							}

							// Then try to parse as JSON
							try {
								const contentArray = JSON.parse(decodedContent);
								if (Array.isArray(contentArray)) {
									messageContents = contentArray;
								}
							} catch (jsonError) {
								// If it's not JSON, create a simple text message content
								messageContents = [
									new MessageContent(
										MessageContentType.Text,
										decodedContent,
										[], // mentions
										{} // formatting
									),
								];
							}
						}
					} catch (error) {
						console.error('Error parsing edited message content:', error);
					}

					// Build author object - handle cases where author data might be incomplete
					const authorDto = {
						id: payload.author?.id || payload.author_id || '',
						name: payload.author?.name || payload.author_name || 'Unknown',
						surname: payload.author?.surname || payload.author_surname || '',
						avatar: payload.author?.avatar || payload.author_avatar || '',
						onlineStatus: payload.author?.onlineStatus || payload.author_online_status || 0,
					};

					// Get the original message to preserve its creation date
					const { getChannelMessages } = useMessageStore.getState();
					const channelMessages = getChannelMessages(payload.channel_id);
					const originalMessage = channelMessages.find(msg => msg.id === id);
					
					// Preserve the original creation timestamp, only use current time for edit timestamp
					const originalTimestamp = originalMessage 
						? originalMessage.createdAt
						: (payload.timestamp || Math.floor(Date.now() / 1000));
					
					const editTimestamp = payload.timestamp || Math.floor(Date.now() / 1000);

					// Create the edited message object in API format
					const editedApiMessage = {
						id,
						channelId: payload.channel_id,
						content: payload.content,
						repliedMessageId: payload.replied_message_id,
						author: authorDto,
						mentions: payload.mentions || [],
						attachments: payload.attachments || [],
						createdAt: originalTimestamp, // Preserve original creation time
						editedAt: editTimestamp, // Track when it was edited
						status: MessageStatus.Edited,
						messageStatus: MessageStatus.Edited, // Add this for the UI to show "edited" indicator
					};

					// Handle with Zustand
					handleEditedMessage(id, payload.channel_id, editedApiMessage);

					// Update lastMessageInfo to trigger channel list update
					setLastMessageInfo(payload.channel_id);
					
					console.log('✅ Edit message processed successfully');
				} else {
					console.error('❌ Edit message missing required data:', { id, channel_id: payload.channel_id, payload });
				}
			}
		},
	});

	const size = useWindowSize();

	const calculatedHeight = useMemo(() => {
		if (size.width <= 576) return `calc(${size.height}px - 67px)`;
		if (size.width <= 993 && size.width > 576) return `calc(${size.height}px - 66px)`;
		if (size.width > 993) return `calc(${size.height}px - 115px)`;

		return `calc(${size.height}px - 92px)`;
	}, [size.height, size.width]);

	const calculatedWidth = useMemo(() => {
		if (size.width <= 993) return '100%';
		if (size.width > 1360) return `calc(${size.width}px - 350px)`;
		// if (size.width > 1360) return `calc(${size.width}px - 350px)`;
	}, [size.width]);

	return (
		<Box className="relative">
			<Box
				display="flex"
				height={calculatedHeight}
				width={calculatedWidth}
				sx={{ backgroundColor: 'chat-bg' }}
			>
				{/* On mobile, only show the sidebar if no channel is selected */}
				{(!isMobile || (isMobile && !selectedChannelId)) && (
					<Box
						sx={{
							width: isMobile ? '100%' : '380px',
							display: 'block',
							height: '100%',
						}}
					>
						<ChatSidebar />
					</Box>
				)}

				{/* On mobile, only show chat window if a channel is selected */}
				{(!isMobile || (isMobile && selectedChannelId)) && (
					<Box
						sx={{
							flex: 1,
							width: isMobile ? '100%' : 'auto',
						}}
					>
						<ChatWindow />
					</Box>
				)}
			</Box>
		</Box>
	);
};
